import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Eye, EyeOff, Loader2 } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Always clear any stale auth state when landing on /login. This fixes the
  // recurring "I can't log in" reports caused by expired/old localStorage tokens
  // and previous-build cookies still hanging around in the browser.
  useEffect(() => {
    try {
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      // Best-effort clear of stale cookies from earlier cookie-based auth phase
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
    } catch { /* no-op */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Trim defensively — password managers occasionally trail whitespace
    const cleanUsername = (username || '').trim();
    const cleanPassword = (password || '').trim();

    try {
      const result = await login(cleanUsername, cleanPassword);
      // Client-role users go directly to their linked Praxis client record
      if (result?.role === 'client' && result?.praxis_client_id) {
        navigate(`/praxis/clients/${result.praxis_client_id}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      // Surface attempted username so the user can spot a typo or autofill issue
      setError(detail.includes('Invalid') ? `${detail} (tried username "${cleanUsername}")` : detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12 bg-[#F9F8F6]">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo & Header */}
          <div className="text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#1E40AF] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-semibold text-[#1C1C1A]" style={{ fontFamily: 'Outfit' }}>
                BusinessHub
              </span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-light tracking-tight text-[#1C1C1A]" style={{ fontFamily: 'Outfit' }}>
              Welcome back
            </h1>
            <p className="mt-2 text-[#5C5C58]">
              Sign in to manage your company expenses
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form" autoComplete="off">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#1C1C1A] font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                data-testid="login-username-input"
                className="h-12 border-[#E6E4E0] focus:border-[#1E40AF] focus:ring-[#1E40AF]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1C1C1A] font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="login-password-input"
                  className="h-12 border-[#E6E4E0] focus:border-[#1E40AF] focus:ring-[#1E40AF]/20 rounded-xl pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#5C5C58] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl font-medium btn-transition"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Clean gradient */}
      <div className="hidden lg:block lg:w-1/2 relative bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A]">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-12">
            <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-light text-white mb-3" style={{ fontFamily: 'Outfit' }}>
              Manage Multiple Companies
            </h2>
            <p className="text-white/70 max-w-sm mx-auto">
              Track expenses, analyze spending, and maintain financial clarity across all your businesses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
